import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import {
  fetchRunlifeDashboard,
  selectFilterDates,
  selectInstallationYears,
  selectSelectedDateType,
  selectSelectedYear,
  selectStartDate,
  selectEndDate,
  setDateFilters,
} from './RunlifeDarshboardSlice';
import { ReactComponent as ChevronDown } from '../../images/chevron-down.svg';
import DateRangePicker from '../../components/date-range-picker/DateRangePicker';
import Helpers from '../../utilities/Helpers';
import { formatDate } from '../../utilities/dateHelpers';
import './RunlifeDashboard.scss';

interface RunLifeFilterProps {
  reportButton?: React.ReactNode;
}

const RunLifeFilter: React.FC<RunLifeFilterProps> = ({ reportButton }) => {
  const years = useAppSelector(selectInstallationYears);
  const filterDates = useAppSelector(selectFilterDates);
  const reduxSelectedYear = useAppSelector(selectSelectedYear);
  const reduxStartDate = useAppSelector(selectStartDate);
  const reduxEndDate = useAppSelector(selectEndDate);
  const reduxSelectedDateType = useAppSelector(selectSelectedDateType);
  const selectedFilters = useAppSelector((state: any) => state?.WellFiltersSlice?.PCSelectedFilters ?? []);
  const pcWellIds = useAppSelector((state) => state.WellFiltersSlice.PCWellIds);

  const [selectedYear, setSelectedYear] = useState<number | undefined>(reduxSelectedYear);
  const [startDate, setStartDate] = useState<string | null>(reduxStartDate);
  const [endDate, setEndDate] = useState<string | null>(reduxEndDate);
  const [selectedDateType, setSelectedDateType] = useState<string | undefined>(reduxSelectedDateType);

  const dispatch = useAppDispatch();
  const yearDropdownRef = useRef<HTMLDivElement | null>(null);
  const dateTypeDropdownRef = useRef<HTMLDivElement | null>(null);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [isDateTypeDropdownOpen, setIsDateTypeDropdownOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);

  const dateRangeRef = useRef<HTMLDivElement | null>(null);

  const yearStart = selectedYear ? new Date(selectedYear, 0, 1) : undefined;
  const yearEnd = selectedYear ? new Date(selectedYear, 11, 31) : undefined;


  const runlifeSidebarOptions = [
    'Today',
    'Yesterday',
    'This week',
    'Last week',
    'This month',
    'Last month',
    'This year',
    'Last year',
    'All time',
  ];

  useEffect(() => {
    setSelectedYear(reduxSelectedYear);
    setStartDate(reduxStartDate);
    setEndDate(reduxEndDate);
    setSelectedDateType(reduxSelectedDateType);
  }, [reduxSelectedYear, reduxStartDate, reduxEndDate, reduxSelectedDateType]);

  // Format helper for date display
  const fmt = (d: Date | null) => (d ? formatDate(d) : '');

  const dateRangeText =
    startDate && endDate
      ? Helpers.toSentenceCase(`${fmt(new Date(startDate))} - ${fmt(new Date(endDate))}`)
      : 'Select date range';

  const getFilterIds = (section: string) =>
    selectedFilters
      .filter((f: any) => f.section === section && typeof f.id === 'number')
      .map((f: any) => f.id)
      .join(',');

  // refresh dashboard manually
  const handleRefresh = async () => {
    dispatch(
      setDateFilters({
        selectedYear,
        startDate,
        endDate,
        selectedDateType,
      }),
    );

    const areaIds = getFilterIds('Area');
    const businessUnitIds = getFilterIds('Business unit');
    const fieldIds = getFilterIds('Field');
    const params: any = {};
    if (areaIds !== '') params.areaIds = areaIds;
    if (businessUnitIds !== '') params.businessUnitIds = businessUnitIds;
    if (fieldIds !== '') params.fieldIds = fieldIds;
    if (pcWellIds && pcWellIds.length > 0) params.wellIds = pcWellIds;
    if (selectedYear) params.year = selectedYear;
    if (selectedDateType) params.dateType = selectedDateType;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    await dispatch(fetchRunlifeDashboard(Object.keys(params).length ? params : undefined));
  };

  // Close the date range popover on outside click or Esc
  useEffect(() => {
    if (!dateRangeOpen) return;

    const handlePointerDown = (e: MouseEvent) => {
      const container = dateRangeRef.current;
      if (container && !container.contains(e.target as Node)) {
        setDateRangeOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDateRangeOpen(false);
    };

    // use capture phase so we catch it before other handlers stopPropagation
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dateRangeOpen]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target as Node)) {
        setIsYearDropdownOpen(false);
      }
      if (dateTypeDropdownRef.current && !dateTypeDropdownRef.current.contains(event.target as Node)) {
        setIsDateTypeDropdownOpen(false);
      }
    };

    if (isYearDropdownOpen || isDateTypeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isYearDropdownOpen, isDateTypeDropdownOpen]);
  return (
    /* ===== Filter controls row ===== */
    <div
      className='runlife-filters-row'
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '39px 18px 18px 18px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Year selector */}
        <div className='view-controls__dropdown' ref={yearDropdownRef}>
          <button
            analytics-id='view-controls-dropdown-btn'
            className='view-controls__dropdown-button runlife-selector__dropdown-button'
            onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
          >
            <span className='runlife-selector__dropdown-text'>
              {selectedYear !== undefined && selectedYear !== null ? selectedYear : 'Select year'}
            </span>
            <div className='view-controls__dropdown-icon'>
              <ChevronDown />
            </div>
          </button>

          {isYearDropdownOpen && (
            <div className='view-controls__dropdown-menu runlife-selector__dropdown-menu'>
              {Array.isArray(years) &&
                [undefined, ...years].map((year) => {
                  const isActive = selectedYear === year;
                  return (
                    <button
                      key={year || 'select'}
                      analytics-id={`view-controls-dropdown-item-${year || 'select-year'}`}
                      className={`view-controls__dropdown-item ${isActive ? 'view-controls__dropdown-item--active' : ''}`}
                      onClick={() => {
                        setSelectedYear(year);
                        if (year) {
                          setStartDate(null);
                          setEndDate(null);
                          setSelectedDateType(undefined);
                        }
                        setIsYearDropdownOpen(false);
                      }}
                    >
                      {year || 'All years'}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        {/* Date range picker */}
        <div ref={dateRangeRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setDateRangeOpen((s) => !s)}
            className='btn-daterangepicker-custom'
            style={{
              minWidth: 220,
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #4A5463',
              color: dateRangeText === 'Select date range' ? 'rgba(255,255,255,0.7)' : 'white',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
              <rect x='3' y='4.5' width='18' height='16' rx='2' stroke='rgba(255,255,255,0.9)' strokeWidth='1.5' />
              <path d='M3 9h18' stroke='rgba(255,255,255,0.9)' strokeWidth='1.5' />
              <path d='M8 2v4M16 2v4' stroke='rgba(255,255,255,0.9)' strokeWidth='1.5' strokeLinecap='round' />
            </svg>
            <span>{Helpers.toSentenceCase(dateRangeText)}</span>
          </button>

          {dateRangeOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% - 20px)',
                left: 0,
                zIndex: 9999,
              }}
            >
              <DateRangePicker
                key={selectedYear ?? 'no-year'}
                className='runlife-date-range-picker-popover'
                initialStartDate={startDate ? new Date(startDate) : yearStart}
                initialEndDate={endDate ? new Date(endDate) : yearEnd}
                showTimeInputs={false}
                sidebarOptions={runlifeSidebarOptions as any}
                onApply={(range: { startDate: Date; endDate: Date }) => {
                  setStartDate(range.startDate ? range.startDate.toISOString() : null);
                  setEndDate(range.endDate ? range.endDate.toISOString() : null);
                  setSelectedYear(undefined);
                  setDateRangeOpen(false);
                }}
                onCancel={() => setDateRangeOpen(false)}
              />
            </div>
          )}
        </div>

        {/* Date type selector */}
        <div className='view-controls__dropdown' ref={dateTypeDropdownRef}>
          <button
            analytics-id='view-controls-dropdown-btn'
            className='view-controls__dropdown-button runlife-selector__dropdown-button'
            onClick={() => setIsDateTypeDropdownOpen(!isDateTypeDropdownOpen)}
            disabled={!(startDate && endDate)}
            style={{
              opacity: startDate && endDate ? 1 : 0.5,
              cursor: startDate && endDate ? 'pointer' : 'not-allowed',
            }}
          >
            <span className='runlife-selector__dropdown-text'>
              {Helpers.toSentenceCase(filterDates?.find((d: any) => d.Key == selectedDateType)?.Value) || 'Date type'}
            </span>
            <div className='view-controls__dropdown-icon'>
              <ChevronDown />
            </div>
          </button>

          {isDateTypeDropdownOpen && (
            <div className='view-controls__dropdown-menu runlife-selector__dropdown-menu'>
              {Array.isArray(filterDates) &&
                [{ Key: undefined, Value: 'Date type' }, ...filterDates].map((dateOption) => {
                  const isActive = selectedDateType === dateOption.Key;
                  return (
                    <button
                      key={dateOption.Key || 'select'}
                      className={`view-controls__dropdown-item ${isActive ? 'view-controls__dropdown-item--active' : ''}`}
                      onClick={() => {
                        setSelectedDateType(dateOption.Key || undefined);
                        setIsDateTypeDropdownOpen(false);
                      }}
                    >
                      {Helpers.toSentenceCase(dateOption.Value)}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        {/* Refresh button */}
        <button onClick={handleRefresh} className='btn-refresh-custom'>
          {' '}
          Refresh{' '}
        </button>
      </div>
      {reportButton}
    </div>
  );
};

export default RunLifeFilter;
